/**
 * Query retry policy — T-20260826-1.
 *
 * The QueryClient default used to be a bare `retry: 1`, which doubled the
 * request count during a rate-limit event: the ONE error class that must never
 * be retried blindly. nginx `limit_req` sends no `Retry-After`, so there is no
 * header to honour — the only correct answer is to stop.
 *
 * Extracted from `App.tsx` so the predicate is unit-testable without mounting
 * the whole app.
 */

import axios from 'axios';
import { TaskApiError } from '@/lib/api/task-errors';

/**
 * The status carried by either error shape the app throws.
 *
 * `TaskApiError` (`lib/api/task-errors.ts`) carries a `status` number directly
 * — including `0` for "no response at all", which `throwFromAxiosError` sets on
 * a network failure. Raw `AxiosError`s reach the query cache from the many call
 * sites that never pass through `throwFromAxiosError`, and there the status
 * lives on `response.status`; an axios error with NO response is also a network
 * failure and reports `0`, matching `TaskApiError`'s convention.
 *
 * Any other `Error` that carries its OWN finite numeric `status` is read too
 * (duck-typed, so this module imports no API module): `HttpStatusError` from
 * the fetch-based query functions (DW-301), `ApiKeyError`, and a
 * `DashboardApiError` whose status is known.
 *
 * Returns `null` when no status can be read — an ordinary `Error`, one whose
 * `status` is `null`/missing/non-numeric, a thrown string — so the caller can
 * fall back to its default policy rather than guessing a status.
 */
export function readErrorStatus(err: unknown): number | null {
  if (err instanceof TaskApiError) {
    return err.status;
  }
  if (axios.isAxiosError(err)) {
    return err.response?.status ?? 0;
  }
  if (err instanceof Error && Object.prototype.hasOwnProperty.call(err, 'status')) {
    const status: unknown = (err as { status?: unknown }).status;
    if (typeof status === 'number' && Number.isFinite(status)) return status;
  }
  return null;
}

/** Failures we refuse to retry. 429 is the storm amplifier this task exists for. */
const NO_RETRY_STATUSES = new Set([429]);

/**
 * TanStack Query `retry` predicate factory (DW-300).
 *
 * Refuses every status on the no-retry list and otherwise retries up to
 * `maxRetries` times. `failureCount` is the number of retries ALREADY
 * attempted — query-core calls this with `0` on the first failure and
 * increments afterwards (`retryer.ts:177-186`) — so `failureCount < maxRetries`
 * reproduces a bare numeric `retry: maxRetries` for everything else.
 *
 * A query hook that wants its own retry budget must use this, not a bare
 * number: a numeric `retry:` bypasses the no-retry list and retries a 429.
 */
export function retryUpTo(maxRetries: number) {
  return (failureCount: number, err: unknown): boolean => {
    const status = readErrorStatus(err);
    if (status !== null && NO_RETRY_STATUSES.has(status)) return false;
    return failureCount < maxRetries;
  };
}

/**
 * The QueryClient default: `retryUpTo(1)` — exactly one retry, as the previous
 * bare `retry: 1` did, for everything not on the no-retry list.
 */
export const shouldRetryQuery: (failureCount: number, err: unknown) => boolean = retryUpTo(1);
